import type { z } from '@customerio/hydra-validation';
import { sessionsStore, changeToSession } from '@customerio/hydra-session-state';
import { PENDING_SESSION_SWITCH_KEY, EXPIRED_SESSION_SNACKBAR_KEY } from '@/features/account-switcher/constants';
import type { ExpiredSessionSnackbar } from '../hooks/use-expired-session-snackbar';

interface PendingSessionSwitch {
  id: number;
  name: string;
}

function getPendingSessionSwitch(): PendingSessionSwitch | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SESSION_SWITCH_KEY);
    if (raw == null) {
      return null;
    }
    const parsed = JSON.parse(raw) as PendingSessionSwitch;
    if (typeof parsed.id !== 'number' || typeof parsed.name !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  } finally {
    sessionStorage.removeItem(PENDING_SESSION_SWITCH_KEY);
  }
}

/**
 * When a session switch results in a 401 (the server rejected the session the
 * client thought was valid), this function detects the pending switch marker in
 * sessionStorage and attempts to fall back to the next valid session.
 *
 * Call this when auth has failed (auth.data is null) and a redirect to /login
 * is about to happen. It will either:
 * - Switch to a valid fallback session, store snackbar info, and reload (returns true)
 * - Return false if there's no pending switch or no valid fallback, allowing
 *   the normal login redirect to proceed.
 */
export function handleExpiredSessionFallback(): boolean {
  const pendingSwitch = getPendingSessionSwitch();
  if (pendingSwitch == null) {
    return false;
  }

  // Find a fallback session: the lowest-ID session that is not the failed one
  // and is not expired
  const allSessions = sessionsStore.get();
  const nowSeconds = Math.floor(Date.now() / 1000);

  const fallbackSession = allSessions
    .filter((session) => session.id !== pendingSwitch.id && session.expires_at >= nowSeconds)
    .sort((a, b) => a.id - b.id)[0];

  if (fallbackSession == null) {
    // No valid fallback — let the normal login redirect handle it
    return false;
  }

  // Store snackbar info so it can be shown after the next reload
  sessionStorage.setItem(
    EXPIRED_SESSION_SNACKBAR_KEY,
    JSON.stringify({ name: pendingSwitch.name } satisfies z.infer<typeof ExpiredSessionSnackbar>),
  );

  changeToSession(fallbackSession.id);
  window.location.assign('/');

  // Return true to indicate a redirect is in progress
  return true;
}
